import { type Size, type ThreeEvent } from '@react-three/fiber';
import { useMachine } from '@xstate/react';
import { useCallback } from 'react';
import { fromPromise } from 'xstate';
import { getPosFromVector } from '@/components/property-selector/utils';
import { machine, input, saveShapeInput } from '@/state-machine/property-selector/machine';

export function usePropertySelectorMachine(
  { shapes, saveShape } = { ...input, saveShape: saveShapeInput },
) {
  const [state, send] = useMachine(
    machine.provide({
      actors: {
        saveShape: fromPromise(saveShape),
      },
    }),
    {
      input: {
        shapes,
      },
    },
  );

  const onDraw = useCallback(() => {
    send({ type: 'DRAW' });
  }, [send]);

  const onSelect = useCallback(() => {
    send({ type: 'SELECT' });
  }, [send]);

  const onPan = useCallback(() => {
    send({ type: 'PAN' });
  }, [send]);

  const onPointerDown = useCallback(
    (event: ThreeEvent<PointerEvent>, size: Size) => {
      send({
        type: 'POINTER_DOWN',
        point: getPosFromVector(event.point, size),
      });
    },
    [send],
  );

  const onPointerUp = useCallback(() => {
    send({ type: 'POINTER_UP' });
  }, [send]);

  const onPointerMove = useCallback(
    (event: ThreeEvent<PointerEvent>, size: Size) => {
      send({
        type: 'POINTER_MOVE',
        point: getPosFromVector(event.point, size),
      });
    },
    [send],
  );

  const onPointerOver = useCallback(() => {
    send({
      type: 'POINTER_OVER',
    });
  }, [send]);

  const onPointerOut = useCallback(() => {
    send({
      type: 'POINTER_OUT',
    });
  }, [send]);

  const onZoomChange = useCallback(
    (newZoomLevel: number) => {
      send({
        type: 'ZOOM_CHANGE',
        zoomLevel: newZoomLevel,
      });
    },
    [send],
  );

  const onDeleteShape = useCallback(() => {
    send({ type: 'DELETE_SHAPE' });
  }, [send]);

  const onReset = useCallback(() => {
    send({ type: 'RESET' });
  }, [send]);

  return {
    state,
    send,
    handlers: {
      onDraw,
      onPan,
      onReset,
      onSelect,
      onPointerDown,
      onPointerUp,
      onPointerMove,
      onPointerOver,
      onPointerOut,
      onZoomChange,
      onDeleteShape,
    },
  };
}
